import { useMutation } from '@tanstack/react-query';
import { Form, Image } from 'antd';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { authApi } from '../../../../apis';
import { LoginDto, UserRoleEnum } from '../../../../apis/client-axios';
import ButtonWrap from '../../../../components/ButtonWrap';
import SvgIconUserSignIn from '../../../../components/svg/SvgOutlined';
import SvgPassword from '../../../../components/svg/SvgPassword';
import ToastError from '../../../../components/Toast/ToastError';
import { ADMIN_ROUTE_NAME } from '../../../../constants/RouteName';
import { AppDispatch, RootState } from '../../../../store';
import { login } from '../../../../store/authSlice';
import CustomInputAuthen from '../../../../components/InputAuthencation';
import './style.scss';

const SignInAdmin = () => {
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const intl = useIntl();
  const logo = useSelector((state: RootState) => state.setting.logo);
  const loginMutation = useMutation({
    mutationFn: (loginDto: LoginDto) => authApi.authControllerLoginAdmin(loginDto),
    onSuccess: async ({ data }) => {
      try {
        dispatch(login(data));
        const roleResponse = await authApi.authControllerProfile();
        const userRole = roleResponse?.data?.data?.role;

        // Navigate based on user role
        switch (userRole) {
          case UserRoleEnum.SuperAdmin:
            navigate(`/${ADMIN_ROUTE_NAME.STAFF_MANAGEMENT}`);
            break;
          case UserRoleEnum.Manager:
            navigate(`/${ADMIN_ROUTE_NAME.ATTENDANCE_MANAGEMENT}`);
            break;
          case UserRoleEnum.Employee:
            navigate(`/${ADMIN_ROUTE_NAME.EMPLOYEE_ATTENDANCE}`);
            break;
          default:
            ToastError({ content: intl.formatMessage({ id: 'sigin.unauthorized' }) });
        }
      } catch (error) {
        ToastError({ content: intl.formatMessage({ id: 'common.errorOccurred' }) });
      }
    },
    onError: () => {
      ToastError({ content: intl.formatMessage({ id: 'sigin.emailOrPasswordWrong' }) });
    },
  });

  const onFinish = (values: LoginDto) => {
    loginMutation.mutate(values); // Pass values directly to mutate
  };

  return (
    <div className="row justify-content-center align-items-center main_sign-in">
      <div className={`image-sign-in${logo === 'images/mos-main.png' || !logo ? ' image-sign-in-default' : ''}`}>
        {
          <Image
            width={200}
            height={200}
            src={logo || 'images/mos-main.png'}
            alt={logo || 'images/mos-main.png'}
            fallback="images/mos-main.png"
            preview={false}
          />
        }
      </div>
      <Form
        name="basic"
        layout="vertical"
        onFinish={onFinish}
        autoComplete="off"
        requiredMark={false}
        className="sign-in"
      >
        <h1 className="sign-in__title">{intl.formatMessage({ id: 'sigin.hello' })}</h1>
        <div className="sign-in__container">
          <div className="sign-in__container-input">
            <Form.Item
              name="employeeCode"
              rules={[{ required: true, message: intl.formatMessage({ id: 'common.require' }) }]}
            >
              <CustomInputAuthen
                placeholder={intl.formatMessage({ id: 'sigin.userID' })}
                prefix={<SvgIconUserSignIn />}
                height={48}
                width={342}
              />
            </Form.Item>
            <Form.Item
              name="password"
              rules={[{ required: true, message: intl.formatMessage({ id: 'common.require' }) }]}
            >
              <CustomInputAuthen
                placeholder={intl.formatMessage({ id: 'sigin.password' })}
                type="password"
                prefix={<SvgPassword />}
                height={48}
                width={342}
              />
            </Form.Item>
          </div>
        </div>
        <Form.Item>
          <ButtonWrap
            disabled={loginMutation.isPending}
            htmlType="submit"
            variant="filled"
            color="primary"
            width={342}
            height={56}
          >
            {intl.formatMessage({ id: 'sigin.submitLogin' })}
          </ButtonWrap>
        </Form.Item>
      </Form>
    </div>
  );
};

export default SignInAdmin;
